"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "~/lib/utils";

interface CardProps extends useRender.ComponentProps<"div"> {}

/** The raised white surface of a card, for elements that can't render through Card. */
export const cardSurfaceClassName = cn(
  "ring-1 ring-gray-900/5",
  "bg-white shadow-[0_1px_5px_-4px_rgba(19,19,22,0.7),0_4px_8px_rgba(32,42,54,0.05)]",
  "dark:bg-[var(--surface-100)] dark:shadow-[0_-1px_rgba(255,255,255,0.06),0_4px_8px_rgba(0,0,0,0.05),0_0_0_1px_rgba(255,255,255,0.1),0_1px_6px_-4px_#000]",
);

function Card({ className, render, ...props }: CardProps) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(
          "relative w-full overflow-hidden rounded-md",
          cardSurfaceClassName,
          className,
        ),
      },
      props,
    ),
    render,
  });
}

function CardScrollingContent({ className, ...props }: React.ComponentProps<"div">) {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    const content = contentRef.current;
    if (content === null) return;
    const checkOverflow = () =>
      setIsOverflowing(
        content.scrollHeight > content.clientHeight &&
          content.scrollTop + content.clientHeight < content.scrollHeight,
      );
    checkOverflow();
    content.addEventListener("scroll", checkOverflow);
    window.addEventListener("resize", checkOverflow);
    return () => {
      content.removeEventListener("scroll", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [props.children]);

  return (
    <>
      <div className={cn("overflow-auto", className)} ref={contentRef} {...props} />
      {isOverflowing ? (
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-8 bg-linear-to-t from-white to-transparent" />
      ) : null}
    </>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col space-y-0.5 p-4 pb-3", className)} {...props} />;
}

function CardTitle({ className, ...props }: React.ComponentProps<"h3">) {
  return (
    <h3 className={cn("text-base font-medium text-[var(--text-primary)]", className)} {...props} />
  );
}

function CardDivider({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("h-px bg-gray-100 dark:bg-gray-900", className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<"p">) {
  return <p className={cn("text-sm text-[var(--text-secondary)]", className)} {...props} />;
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("p-4", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex items-center p-4", className)} {...props} />;
}

const rootVariants = cva(
  "relative isolate overflow-hidden rounded-md py-0.5 [--card-item-p:0.25rem] [--card-p:0.25rem] px-[var(--card-body-px)]",
  {
    variants: {
      background: {
        "surface-50": "bg-[var(--surface-50)]",
        "surface-100": "bg-[var(--surface-100)] dark:bg-[var(--surface-200)]",
      },
      spacing: {
        none: "[--card-body-px:0] [--card-body-py:0]",
        compact: "[--card-body-px:2px] [--card-body-py:0px]",
        cozy: "[--card-body-px:2rem] [--card-body-py:1.5rem]",
      },
    },
    defaultVariants: { background: "surface-100", spacing: "compact" },
  },
);

interface CardRootProps
  extends React.ComponentProps<"section">, VariantProps<typeof rootVariants> {}

function CardRoot({ className, spacing, background, ...props }: CardRootProps) {
  return <section className={cn(rootVariants({ spacing, background }), className)} {...props} />;
}

function CardRootHeader({ className, ...props }: React.ComponentProps<"header">) {
  return <header className={cn("space-y-1 p-4", className)} {...props} />;
}

function CardRootFooter({ className, ...props }: React.ComponentProps<"footer">) {
  return <footer className={cn("px-4 py-4", className)} {...props} />;
}

function CardRootTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex text-base leading-none font-semibold text-[var(--text-primary)]",
        className,
      )}
      {...props}
    />
  );
}

function CardRootText({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex text-sm text-[var(--text-secondary)]", className)} {...props} />;
}

function CeramicCard({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "relative block w-full overflow-hidden rounded-lg",
        "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
        "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-purple-500 dark:before:hidden",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardContent,
  CardDescription,
  CardDivider,
  CardFooter,
  CardHeader,
  CardRoot,
  CardRootFooter,
  CardRootHeader,
  CardRootText,
  CardRootTitle,
  CardScrollingContent,
  CardTitle,
  CeramicCard,
  rootVariants,
};
export type { CardProps, CardRootProps };
